/**
 * ErrorBoundary — top-level catch-all so a runtime exception inside the
 * React tree shows a readable fallback instead of a white screen.
 *
 * Class component because that's still the only React API that gets
 * componentDidCatch. The fallback offers two recovery paths:
 *   - Reload the page (hard reset; usually fixes transient state issues)
 *   - Return to home (soft reset; reroute and remount the page)
 *
 * When Sentry is wired in, push the error there from componentDidCatch.
 */

import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("Top-level error caught by boundary:", error, info);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHome = (): void => {
    // Hard navigate so the React tree fully unmounts, clearing any
    // poisoned state that might have caused the original failure.
    window.location.href = "/";
  };

  render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="max-w-lg w-full" role="alert">
          <p className="font-mono text-xs uppercase tracking-widest text-accent mb-3">
            something broke
          </p>
          <h1 className="font-serif text-3xl font-medium text-ink mb-3">
            That wasn&apos;t supposed to happen.
          </h1>
          <p className="font-serif italic text-lg text-inkSoft mb-6">
            An unexpected error stopped the app. Your saved progress is in
            local storage and is unaffected — try reloading.
          </p>
          <details className="mb-6 border border-ink/10 bg-paperWarm p-4 text-sm text-inkSoft">
            <summary className="cursor-pointer font-mono text-xs uppercase tracking-widest text-inkFaded">
              technical details
            </summary>
            <pre className="mt-3 whitespace-pre-wrap break-words font-mono text-xs text-ink">
              {this.state.error.message}
            </pre>
          </details>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-5 py-2.5 bg-ink text-paper hover:bg-accent transition-colors font-medium"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-5 py-2.5 bg-transparent border border-ink/20 hover:bg-ink hover:text-paper transition-colors"
            >
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
